<template>
  <v-dialog
    v-model="dialogOpen"
    v-if="dialogOpen"
    max-width="650"
    scrollable>
    <v-card class="glass-card">
      <!-- Header with Gradient -->
      <v-card-title class="glass-header pa-4">
        <v-icon color="white" size="28" class="mr-2">mdi-note-text</v-icon>
        <span class="header-text">Napomene za stol</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 card-content">
        <!-- Nema napomena -->
        <div v-if="allTableNotes.length === 0" class="empty-state pa-6">
          <v-icon size="64" color="rgba(57, 137, 201, 0.3)">mdi-note-text</v-icon>
          <p class="empty-text mt-4">Nema stolnih napomena za ovaj stol.</p>
        </div>

        <!-- Lista stolnih napomena -->
        <v-list v-else two-line class="notes-list pa-0">
          <template v-for="(noteItem, index) in allTableNotes">
            <v-list-item
              :key="`note-${index}`"
              class="note-item py-3"
              :class="{ 'current-note': noteItem.isCurrent }">
              <v-list-item-avatar class="note-avatar">
                <div class="avatar-icon" :class="{ 'current-avatar': noteItem.isCurrent }">
                  <v-icon color="white" size="24">
                    mdi-note-text
                  </v-icon>
                </div>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="note-message mb-2">
                  {{ noteItem.message }}
                </v-list-item-title>
                <v-list-item-subtitle class="note-meta">
                  <v-chip
                    v-if="noteItem.isCurrent"
                    x-small
                    color="orange darken-1"
                    text-color="white"
                    class="mr-2 current-chip">
                    Trenutna
                  </v-chip>
                  <span v-if="noteItem.addedBy" class="meta-item">{{ noteItem.addedBy }}</span>
                  <span v-if="noteItem.timestamp" class="meta-item">{{ formatDate(noteItem.timestamp) }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-divider
              v-if="index < allTableNotes.length - 1"
              :key="`divider-${index}`"
              class="note-divider">
            </v-divider>
          </template>
        </v-list>
      </v-card-text>

      <!-- Footer -->
      <v-divider class="footer-divider"></v-divider>
      <v-card-actions class="pa-4 footer-actions">
        <v-spacer></v-spacer>
        <v-btn
          class="close-footer-btn"
          elevation="0"
          large
          @click="close">
          Zatvori
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { df } from '@/plugins/firebase'
import state from '@/state'

export default {
  data () {
    return {
      dialogOpen: false,
      currentTable: null,
      allTableNotes: [] // Merged list of all table notes
    }
  },

  methods: {
    async open (table) {
      this.currentTable = table
      await this.loadAllTableNotes()
      this.dialogOpen = true
    },

    close () {
      this.dialogOpen = false
      this.allTableNotes = []
    },

    async loadAllTableNotes () {
      this.allTableNotes = []

      if (!this.currentTable || !this.currentTable.id) return

      try {
        const tableDoc = await df.doc(
          `location_orders/${state.getPointOfSale().id}/tables/${this.currentTable.id}`
        ).get()

        if (!tableDoc.exists) return

        const tableData = tableDoc.data()

        // 1. Dodaj trenutnu stolnu napomenu (ako postoji)
        if (tableData.notes && tableData.notes.length > 0) {
          const currentStolNote = tableData.notes.find(note =>
            note.unitId === 'stol' ||
            note.unitName === 'Stol' ||
            note.isTableNote === true
          )

          if (currentStolNote) {
            this.allTableNotes.push({
              message: currentStolNote.message,
              isCurrent: true,
              timestamp: tableData.note_updated_at || Date.now(),
              addedBy: tableData.note_updated_by || 'N/A'
            })
          }
        }

        // 2. Dodaj stolne napomene iz povijesti (orders)
        if (tableData.orders && tableData.orders.length > 0) {
          const orders = [...tableData.orders]
          // Sortiraj od najnovijih prema najstarijim
          orders.sort((a, b) =>
            (b.order_number || 0) - (a.order_number || 0)
          )

          orders.forEach(order => {
            if (order.notes && order.notes.length > 0) {
              order.notes.forEach(note => {
                // Samo stolne napomene
                if (
                  note.unitId === 'stol' ||
                  note.unitName === 'Stol' ||
                  note.isTableNote === true
                ) {
                  this.allTableNotes.push({
                    message: note.message,
                    isCurrent: false,
                    timestamp: order.created_at || order.created || null,
                    addedBy: order.added_by_name || 'N/A',
                    orderNumber: order.order_number,
                    orderStatus: order.order_status
                  })
                }
              })
            }
          })
        }
      } catch (error) {
        console.error('Error loading table notes:', error)
      }
    },

    formatDate (timestamp) {
      if (!timestamp) return ''

      let date
      if (timestamp.toDate) {
        // Firebase Timestamp
        date = timestamp.toDate()
      } else if (timestamp < 10000000000) {
        // Unix timestamp u sekundama
        date = new Date(timestamp * 1000)
      } else {
        // Već u milisekundama
        date = new Date(timestamp)
      }

      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      return `${day}.${month}.${year} ${hours}:${minutes}`
    }
  }
}
</script>

<style scoped>
/* Glass Card Style */
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 8px 32px rgba(57, 137, 201, 0.15) !important;
  overflow: hidden;
}

/* Header with Info Gradient */
.glass-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  position: relative;
  border-bottom: 1px solid rgba(57, 137, 201, 0.3);
}

.glass-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
}

.header-text {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: white !important;
  letter-spacing: 0.3px !important;
  position: relative;
  z-index: 1;
}

.close-btn {
  position: relative;
  z-index: 1;
  transition: all 0.2s ease !important;
}

.close-btn:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Card Content */
.card-content {
  max-height: 500px;
  overflow-y: auto;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 48px 24px !important;
}

.empty-text {
  font-size: 15px;
  font-weight: 500;
  color: rgba(57, 137, 201, 0.7) !important;
  letter-spacing: 0.3px;
  margin: 0;
}

/* Notes List */
.notes-list {
  background: transparent !important;
}

/* Note Item */
.note-item {
  transition: all 0.2s ease !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
  min-height: 80px !important;
}

.note-item:hover {
  background: rgba(57, 137, 201, 0.05) !important;
}

.current-note {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 100%) !important;
}

.current-note:hover {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.12) 0%,
    rgba(57, 137, 201, 0.1) 100%) !important;
}

/* Avatar Icon */
.note-avatar {
  margin-right: 16px !important;
}

.avatar-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg,
    rgba(120, 120, 130, 0.9) 0%,
    rgba(90, 90, 100, 0.85) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.current-avatar {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 100%) !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.3);
}

/* Note Message */
.note-message {
  font-size: 17px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  line-height: 1.5 !important;
  white-space: normal !important;
  word-break: break-word !important;
  letter-spacing: 0.3px !important;
}

/* Note Meta */
.note-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.meta-item {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(120, 120, 130, 0.8) !important;
  letter-spacing: 0.2px !important;
}

.meta-item::before {
  content: '•';
  margin: 0 6px;
  color: rgba(120, 120, 130, 0.5);
}

.meta-item:first-of-type::before {
  display: none;
}

/* Current Chip */
.current-chip {
  font-size: 11px !important;
  font-weight: 600 !important;
  height: 20px !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 2px 4px rgba(57, 137, 201, 0.2) !important;
}

/* Note Divider */
.note-divider {
  border-color: rgba(57, 137, 201, 0.12) !important;
  margin: 0 16px !important;
}

/* Footer */
.footer-divider {
  border-color: rgba(57, 137, 201, 0.2) !important;
}

.footer-actions {
  background: rgba(57, 137, 201, 0.02) !important;
}

/* Close Button in Footer */
.close-footer-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
  height: 44px !important;
  min-width: 120px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.close-footer-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  transform: translateY(-1px);
}

/* Scrollbar Styling */
.card-content::-webkit-scrollbar {
  width: 8px;
}

.card-content::-webkit-scrollbar-track {
  background: rgba(57, 137, 201, 0.05);
  border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
  background: rgba(57, 137, 201, 0.3);
  border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
  background: rgba(57, 137, 201, 0.5);
}
</style>
